import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import '../../../component-styles/PublicProposalPage.css';
import API_BASE from '../../../utils/api.js';
import PublicStripePaymentModal from '../Invoices/PublicStripePaymentModal';
import notify from '../../../utils/notify';
import { visibleCustomSpecs } from '../../../utils/customSpecs.js';
import {
  getProposalBalanceDue,
  getProposalInvoicedAmount,
  getProposalInvoicingStatus,
  isInvoicedLineItem,
} from '../../../utils/proposalInvoicing.js';

const ERROR_MESSAGES = {
  expired:
    'This proposal link has expired. Please reach out to your designer to request a new one.',
  not_found:
    'This proposal link is no longer active. Please contact your designer for an updated link.',
  network:
    "We couldn't load this proposal right now. Please try refreshing the page, or contact your designer if the problem continues.",
};

function fmt(value) {
  return Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDimensions(dim) {
  if (!dim || typeof dim !== 'object') return '';
  const parts = [];
  if (dim.width) parts.push(`${dim.width}"W`);
  if (dim.length) parts.push(`${dim.length}"L`);
  if (dim.height) parts.push(`${dim.height}"H`);
  if (dim.depth) parts.push(`${dim.depth}"D`);
  return parts.join(' x ');
}

function formatColorMaterial(it) {
  return [it.color, it.finishes, it.materials].filter(Boolean).join(' / ');
}

function ItemApprovalToggle({ status, isLocked, onAccept, onDecline, onReset }) {
  if (isLocked) {
    if (status === true) return <span className="item-status-badge item-status--accepted">Accepted</span>;
    if (status === false) return <span className="item-status-badge item-status--declined">Declined</span>;
    return null;
  }

  if (status === true) {
    return (
      <button className="item-status-badge item-status--accepted" onClick={onReset} title="Click to undo">
        ACCEPTED
      </button>
    );
  }
  if (status === false) {
    return (
      <button className="item-status-badge item-status--declined" onClick={onReset} title="Click to undo">
        DECLINED
      </button>
    );
  }
  return (
    <div className="item-approval-actions">
      <button className="item-approve-btn" onClick={onAccept}>Accept</button>
      <button className="item-decline-btn" onClick={onDecline}>Decline</button>
    </div>
  );
}

export default function PublicProposalPage() {
  const { shareToken: routeShareToken } = useParams();
  const location = useLocation();
  const pdfRef = useRef(null);

  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Map of itemId → selectedAltId (null = main item selected)
  const [selections, setSelections] = useState({});
  // Map of itemId → true (accepted) | false (declined) | null (undecided)
  const [itemApprovals, setItemApprovals] = useState({});
  const [showPayment, setShowPayment] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [specConfirmed, setSpecConfirmed] = useState(false);
  const shareToken = routeShareToken || new URLSearchParams(location.search).get('publicProposal');

  /* -----------------------------------------------------
     LOAD PROPOSAL
  ----------------------------------------------------- */
  useEffect(() => {
    if (!shareToken) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/proposals/public/${shareToken}`);
        if (res.status === 410) throw new Error('expired');
        if (!res.ok) throw new Error('not_found');
        const data = await res.json();
        setProposal(data);

        const initialSelections = {};
        const initialApprovals = {};
        for (const li of data.lineItems || []) {
          if (li.selectedAltId !== undefined) initialSelections[li.itemId] = li.selectedAltId;
          initialApprovals[li.itemId] = li.clientApproved ?? null;
        }
        setSelections(initialSelections);
        setItemApprovals(initialApprovals);
      } catch (err) {
        setError(err.message || 'network');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareToken, API_BASE]);

  /* -----------------------------------------------------
     DYNAMIC PAGE TITLE
  ----------------------------------------------------- */
  useEffect(() => {
    if (!proposal) return;
    const num = proposal.proposalNumber ? `Proposal #${proposal.proposalNumber}` : 'Proposal';
    const firm = proposal.companyName ? ` — ${proposal.companyName}` : '';
    document.title = `${num}${firm}`;
    return () => {
      document.title = 'InteriorFolio';
    };
  }, [proposal]);

  /* -----------------------------------------------------
     SAFE DEFAULTS (BEFORE useMemo)
  ----------------------------------------------------- */
  const lineItems = proposal?.lineItems || [];
  const totals = proposal?.totals || {};

  /* -----------------------------------------------------
     COMPUTED TOTALS — excludes declined items, live-updated by selections
  ----------------------------------------------------- */
  const computed = useMemo(() => {
    // Line totals include each item's shipping + tariff; those are pulled back
    // out so the summary can list them separately from the merchandise subtotal.
    let itemsTotal = 0;
    let shipping = 0;
    let tariff = 0;
    for (const li of lineItems) {
      if (itemApprovals[li.itemId] === false) continue;

      const selAltId = selections[li.itemId] ?? li.selectedAltId ?? null;
      const alt = selAltId ? (li.alternatives || []).find((a) => a.altId === selAltId) : null;
      const it = alt || li;
      const itShipping = Number(it.clientShipping || 0);
      const itTariff = Number(it.clientTariff || 0);
      itemsTotal += Number(
        it.lineTotal ?? Number(it.qty || 1) * Number(it.clientUnit || 0) + itShipping + itTariff
      );
      shipping += itShipping;
      tariff += itTariff;
    }

    const subtotal = itemsTotal - shipping - tariff;
    const tax = Number(totals.tax || 0);
    const procurementFee = Number(totals.procurementFee || 0);
    const grandTotal = itemsTotal + tax + procurementFee;
    const hasDeclined = Object.values(itemApprovals).some((v) => v === false);

    return { subtotal, shipping, tariff, tax, procurementFee, grandTotal, hasDeclined };
  }, [lineItems, totals.tax, totals.procurementFee, selections, itemApprovals]);

  function formatStatus(status) {
    if (!status) return '';
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /* -----------------------------------------------------
     EARLY RETURNS
  ----------------------------------------------------- */
  if (loading) return <div className="public-loading">Loading proposal…</div>;
  if (error)
    return (
      <div className="public-error">
        {ERROR_MESSAGES[error] || ERROR_MESSAGES.network}
      </div>
    );
  if (!proposal) return null;

  const {
    client = {},
    billingAddress,
    shippingAddress,
    projectName,
    proposalNumber,
    status,
    coverImageUrl,
    companyName,
    logoUrl,
  } = proposal;

  const tpl = proposal.proposalTemplate || {};
  const accentColor = tpl.accentColor || '#a5d27d';
  const showDimensions = tpl.showDimensions !== false;
  const showColorMaterial = tpl.showColorMaterial !== false;
  const showCustomSpecs = tpl.showCustomSpecs !== false;
  const showProductLink = tpl.showProductLink === true;
  const showMsrpSavings = tpl.showMsrpSavings === true;
  const showVendor = tpl.showVendor === true;
  const requireSpecConfirmation = tpl.requireSpecConfirmation === true;
  const requirePaymentOnApproval = tpl.requirePaymentOnApproval !== false;

  const hasPaid = Number(proposal?.totals?.paid || 0) > 0;
  const isLocked = status === 'approved' && hasPaid;
  // Backend rejects item-approval changes once the proposal is approved,
  // regardless of payment — keep the per-item controls in sync with that.
  const itemsLocked = status === 'approved';
  const proposalPaid = Number(proposal?.totals?.paid || 0);
  // Items already moved onto an invoice are paid there, so they're not part
  // of what's still owed on the proposal (its total stays the same).
  const invoicedAmount = getProposalInvoicedAmount(proposal);
  const invoicingStatus = getProposalInvoicingStatus(proposal);
  const remainingBalance = getProposalBalanceDue(proposal);

  /* -----------------------------------------------------
     ALTERNATIVE SELECTION
  ----------------------------------------------------- */
  async function selectAlternative(itemId, altId) {
    const next = { ...selections, [itemId]: altId };
    setSelections(next);

    try {
      const res = await fetch(`${API_BASE}/api/proposals/public/${shareToken}/selections`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selections: Object.entries(next).map(([id, selAltId]) => ({ itemId: id, selectedAltId: selAltId })),
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();

      // Picking an alternative promotes it into the main item slot server-side
      // (and demotes the previous main item into that alternative's spot), so
      // pull the updated items back in to reflect the swap immediately.
      if (Array.isArray(data.lineItems)) {
        setProposal((prev) =>
          prev
            ? { ...prev, lineItems: data.lineItems, totals: { ...prev.totals, ...data.totals } }
            : prev
        );
        const reconciled = {};
        for (const li of data.lineItems) reconciled[li.itemId] = li.selectedAltId ?? null;
        setSelections(reconciled);
      }
    } catch {
      // Selection is already applied locally; backend sync failure is non-blocking
    }
  }

  /* -----------------------------------------------------
     ITEM APPROVAL TOGGLE
  ----------------------------------------------------- */
  async function setItemApproval(itemId, clientApproved) {
    const previous = itemApprovals[itemId] ?? null;
    setItemApprovals((prev) => ({ ...prev, [itemId]: clientApproved }));

    try {
      const res = await fetch(`${API_BASE}/api/proposals/public/${shareToken}/item-approvals`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId, clientApproved }),
      });
      if (!res.ok) throw new Error();
    } catch {
      // Revert — backend rejected the change (e.g. proposal already approved)
      setItemApprovals((prev) => ({ ...prev, [itemId]: previous }));
      notify('This proposal has already been approved, so item selections are locked. Contact your designer to make changes.');
    }
  }

  /* -----------------------------------------------------
     STATUS UPDATE
  ----------------------------------------------------- */
  async function updateStatus(newStatus, extra = {}) {
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/public/${shareToken}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus, ...extra }),
        }
      );

      if (!res.ok) throw new Error();
      notify(`Proposal ${newStatus.replace('_', ' ')}`);
      localStorage.setItem('refreshProposals', '1');
      setProposal((prev) => ({ ...prev, status: newStatus }));
      setShowApproveModal(false);
    } catch {
      notify('Failed to update proposal');
    }
  }

  function handleApproveClick() {
    if (requireSpecConfirmation) {
      setSpecConfirmed(false);
      setShowApproveModal(true);
      return;
    }
    updateStatus('approved');
  }

  /* -----------------------------------------------------
     PDF DOWNLOAD
  ----------------------------------------------------- */
  function downloadPDF() {
    if (!pdfRef.current) return;
    // html2pdf renders a copy of the card into an A4-wide box (~718px) attached to
    // <body>, outside .public-proposal-wrapper — narrower than on screen and without
    // the page's theme variables. .pp-pdf-export restores the theme and fits the
    // table to that width.
    const exportEl = document.createElement('div');
    exportEl.className = 'pp-pdf-export';
    exportEl.style.setProperty('--pp-accent', accentColor);
    exportEl.appendChild(pdfRef.current.cloneNode(true));
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `Proposal-${proposalNumber || proposal.proposalId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        // windowWidth keeps the phone breakpoint (which hides Qty/Rate/Shipping/Tariff)
        // out of PDFs downloaded on a phone.
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff', windowWidth: 1024 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(exportEl)
      .save();
  }

  /* -----------------------------------------------------
     RENDER
  ----------------------------------------------------- */
  return (
    <div className="public-proposal-wrapper" style={{ '--pp-accent': accentColor }}>
      <header className="public-topbar">
        {logoUrl ? (
          <img src={`${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(logoUrl)}`} alt={companyName || 'Your designer'} />
        ) : companyName ? (
          <span className="public-firm-name">{companyName}</span>
        ) : null}
      </header>

      <div className="public-layout">
        <div className="public-content">
          <div id="proposal-content" className="public-proposal-card" ref={pdfRef}>
            <header className="public-header">
              <div>
                <h1>{projectName || 'Proposal'}</h1>
                <div className={`public-status status-${status}`}>{formatStatus(status)}</div>
                {invoicingStatus && (
                  <div
                    className={`public-status status-${invoicingStatus}`}
                    style={{ marginLeft: 6 }}
                  >
                    {formatStatus(invoicingStatus)}
                  </div>
                )}
                {proposalNumber && (
                  <p className="proposal-id">
                    Proposal #{proposalNumber}
                    {proposal.title ? ` · ${proposal.title}` : ''}
                  </p>
                )}
              </div>
            </header>

            <section className="public-info-grid">
              <div>
                <strong>Billing Address</strong>
                <p>{billingAddress || '-'}</p>
              </div>
              <div>
                <strong>Shipping Address</strong>
                <p>{shippingAddress || '-'}</p>
              </div>
              <div>
                <strong>Client Name</strong>
                <p>{client.name || '-'}</p>
              </div>
            </section>

            <section className="public-items-table">
              <table className="proposal-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="col-qty">Qty</th>
                    <th className="col-rate">Rate</th>
                    <th className="col-shipping">Shipping</th>
                    <th className="col-tariff">Tariff</th>
                    <th className="col-total">Total</th>
                    <th className="col-status"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li) => {
                    const isInvoiced = isInvoicedLineItem(li);
                    // An invoiced item's option is settled, so there's nothing left to pick.
                    const hasAlts = !isInvoiced && Array.isArray(li.alternatives) && li.alternatives.length > 0;
                    const selAltId = selections[li.itemId] ?? li.selectedAltId ?? null;
                    const activeAlt = hasAlts && selAltId
                      ? li.alternatives.find((a) => a.altId === selAltId) || null
                      : null;

                    const displayItem = activeAlt || li;
                    const qty = Number(displayItem.qty || 1);
                    const rate = Number(displayItem.clientUnit || 0);
                    const shipping = Number(displayItem.clientShipping || 0);
                    const tariff = Number(displayItem.clientTariff || 0);
                    const total = Number(displayItem.lineTotal ?? qty * rate + shipping + tariff);
                    const approval = itemApprovals[li.itemId] ?? null;
                    const isDeclined = approval === false;

                    return (
                      <React.Fragment key={li.itemId}>
                        <tr className={isDeclined ? 'item-row--declined' : ''}>
                          <td>
                            <div className="item-block">
                              {displayItem.imageUrl && (
                                <img
                                  src={`${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(displayItem.imageUrl)}`}
                                  alt=""
                                  className="item-img"
                                />
                              )}
                              <div className="item-info">
                                <div className="item-title">
                                  {displayItem.clientName || displayItem.name || li.clientName || li.name}
                                </div>
                                {li.room && <div className="item-room">Room: {li.room}</div>}
                                {displayItem.description && (
                                  <div className="item-description">{displayItem.description}</div>
                                )}
                                {showVendor && displayItem.vendor && (
                                  <div className="item-vendor">{displayItem.vendor}</div>
                                )}
                                {showDimensions && formatDimensions(displayItem.dimensions) && (
                                  <div className="item-description">{formatDimensions(displayItem.dimensions)}</div>
                                )}
                                {showColorMaterial && formatColorMaterial(displayItem) && (
                                  <div className="item-description">{formatColorMaterial(displayItem)}</div>
                                )}
                                {showCustomSpecs &&
                                  visibleCustomSpecs(displayItem.customSpecs).map((spec) => (
                                    <div className="item-description" key={spec.id}>
                                      {spec.label}{spec.value ? `: ${spec.value}` : ''}
                                    </div>
                                  ))}
                                {showProductLink && displayItem.link && (
                                  <div className="item-description">
                                    <a href={displayItem.link} target="_blank" rel="noreferrer">View Product</a>
                                  </div>
                                )}
                                {showMsrpSavings && Number(displayItem.msrp) > Number(displayItem.clientUnit) && (
                                  <div className="item-description">
                                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                                      MSRP: {fmt(displayItem.msrp)}
                                    </span>
                                    {' · '}
                                    <span style={{ color: '#4a8a3a', fontWeight: 600 }}>
                                      You save {fmt((Number(displayItem.msrp) - Number(displayItem.clientUnit)) * Number(qty))}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="col-qty">{qty}</td>
                          <td className="col-rate">{fmt(rate)}</td>
                          <td className="col-shipping">{shipping > 0 ? fmt(shipping) : '-'}</td>
                          <td className="col-tariff">{tariff > 0 ? fmt(tariff) : '-'}</td>
                          <td className="col-total grand-cell">{fmt(total)}</td>
                          <td className="col-status">
                            {isInvoiced ? (
                              <span className="item-status-badge item-status--invoiced">Invoiced</span>
                            ) : (
                              <ItemApprovalToggle
                                status={approval}
                                isLocked={itemsLocked}
                                onAccept={() => setItemApproval(li.itemId, true)}
                                onDecline={() => setItemApproval(li.itemId, false)}
                                onReset={() => setItemApproval(li.itemId, null)}
                              />
                            )}
                          </td>
                        </tr>
                        {hasAlts && (
                          <tr className="alt-picker-row">
                            <td colSpan={6}>
                              <div className="alt-picker">
                                <span className="alt-picker-label">Choose option:</span>
                                <div className="alt-picker-tabs">
                                  <button
                                    className={`alt-tab ${!selAltId ? 'alt-tab--active' : ''}`}
                                    onClick={() => selectAlternative(li.itemId, null)}
                                  >
                                    <span className="alt-tab-name">{li.clientName || li.name || 'Main'}</span>
                                    <span className="alt-tab-price">
                                      {fmt(li.lineTotal ?? (li.qty || 1) * (li.clientUnit || 0))}
                                    </span>
                                  </button>

                                  {li.alternatives.map((alt, idx) => {
                                    const altTotal = Number(
                                      alt.lineTotal ?? Number(alt.qty || 1) * Number(alt.clientUnit || 0)
                                    );
                                    return (
                                      <button
                                        key={alt.altId}
                                        className={`alt-tab ${selAltId === alt.altId ? 'alt-tab--active' : ''}`}
                                        onClick={() => selectAlternative(li.itemId, alt.altId)}
                                      >
                                        <span className="alt-tab-name">
                                          {alt.name || `Alt ${idx + 1}`}
                                        </span>
                                        <span className="alt-tab-price">{fmt(altTotal)}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </section>

            <section className="public-totals">
              {computed.hasDeclined && (
                <p className="totals-note">* Declined items excluded from total</p>
              )}
              <p><span>Subtotal</span><span>{fmt(computed.subtotal)}</span></p>
              {computed.shipping > 0 && (
                <p><span>Shipping</span><span>{fmt(computed.shipping)}</span></p>
              )}
              {computed.tariff > 0 && (
                <p><span>Tariff</span><span>{fmt(computed.tariff)}</span></p>
              )}
              {computed.tax > 0 && (
                <p><span>Tax</span><span>{fmt(computed.tax)}</span></p>
              )}
              {computed.procurementFee > 0 && (
                <p><span>Procurement Fee</span><span>{fmt(computed.procurementFee)}</span></p>
              )}
              <p className="grand">Total: {fmt(computed.grandTotal)}</p>
              {invoicedAmount > 0 && (
                <p><span>Invoiced</span><span>{fmt(invoicedAmount)}</span></p>
              )}
              {proposalPaid > 0 && (
                <p><span>Paid</span><span>{fmt(proposalPaid)}</span></p>
              )}
              {remainingBalance > 0 && (
                <p><span>Remaining Balance</span><span>{fmt(remainingBalance)}</span></p>
              )}
            </section>

            {proposal.notes && (
              <section className="public-terms">
                <strong>Notes / Payment Terms</strong>
                <div
                  style={{ whiteSpace: 'pre-wrap', color: '#6b7280', fontSize: 13 }}
                  dangerouslySetInnerHTML={{ __html: proposal.notes }}
                />
              </section>
            )}

            {tpl.termsText && (
              <section className="public-terms">
                <strong>Terms &amp; Conditions</strong>
                <div
                  style={{ whiteSpace: 'pre-wrap', color: '#6b7280', fontSize: 13 }}
                  dangerouslySetInnerHTML={{ __html: tpl.termsText }}
                />
              </section>
            )}
          </div>
        </div>

        {coverImageUrl && (
          <aside className="public-cover" style={{ backgroundImage: `url(${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(coverImageUrl)})` }} />
        )}
      </div>

      {Array.isArray(proposal.attachments) && proposal.attachments.length > 0 && (
        <div className="public-content" style={{ marginTop: -8 }}>
          <div className="public-proposal-card" style={{ padding: '16px 24px' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>Attachments</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {proposal.attachments.map((a) => (
                <a
                  key={a.id || a.url}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 14 }}
                >
                  {a.name || 'Download attachment'}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="public-actions">
        {/* Nothing left to approve once every item has been invoiced. */}
        {!isLocked && invoicingStatus !== 'invoiced' && (
          <button className="approve-btn" onClick={handleApproveClick}>
            Approve
          </button>
        )}
        {/* With pay-on-approval turned off the studio bills through a separate
            invoice, so the proposal never offers payment — not even once approved. */}
        {remainingBalance > 0 && requirePaymentOnApproval && (
          <button className="approve-btn" onClick={() => setShowPayment(true)}>
            Pay Now
          </button>
        )}
        <button className="pdf-btn" onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      {showApproveModal && (
        <div className="pp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowApproveModal(false)}>
          <div className="pp-modal">
            <h2>Approve Proposal</h2>
            <label className="pp-modal-check">
              <input
                type="checkbox"
                checked={specConfirmed}
                onChange={(e) => setSpecConfirmed(e.target.checked)}
              />
              <span>I confirm the dimensions, color/material, and other item details shown in this proposal are correct.</span>
            </label>
            <div className="pp-modal-actions">
              <button className="if-btn-secondary" onClick={() => setShowApproveModal(false)}>
                Cancel
              </button>
              <button
                className="approve-btn"
                disabled={!specConfirmed}
                onClick={() => updateStatus('approved', { specConfirmed })}
              >
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {showPayment && (
        <PublicStripePaymentModal
          shareToken={shareToken}
          createIntentEndpoint={`${API_BASE}/api/proposals/public/${shareToken}/create-payment-intent`}
          applyPaymentEndpoint={`${API_BASE}/api/proposals/public/${shareToken}/apply-payment`}
          remainingBalance={remainingBalance}
          title="Pay Proposal"
          invoiceNumber={proposalNumber}
          onSuccess={(amount) => {
            setProposal((prev) => ({
              ...prev,
              totals: {
                ...prev.totals,
                paid: Number(((prev.totals?.paid ?? 0) + amount).toFixed(2)),
              },
            }));
            setShowPayment(false);
            notify('Payment submitted successfully');
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}
